

// Localização: frontend/src/components/Prontuario/PacienteHeader.jsx (versão 0.2.2)

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usePacienteProntuario } from '../../hooks/data/usePacienteProntuario'; 
import { usePacientePagamentoStatus } from '../../hooks/usePacientePagamentoStatus'; 
import { formatDate } from '../../utils/formatters'; 
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils'; 
import LoadingIndicator from '../Common/LoadingIndicator'; 
import Alert from '../UI/Alert'; 
import { gerarProntuarioPDF } from '../../utils/gerarProntuarioPDF';
import { 
    CheckCircle2, 
    AlertCircle, 
    Clock, 
    Pencil, 
    CloudDownload,
    Pill,
    Calendar,
    Thermometer,
    Activity,
    AlertTriangle,
    Heart,
    Stethoscope,
    FileText,
    User,
    ArrowLeft,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import { usePermissions } from '../../context/PermissionContext';


// Define a URL do avatar de fallback (deve corresponder ao imageUtils.js)
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png'; 


// 🚀 SUBCOMPONENTE OTIMIZADO: MOVIDO PARA FORA DO COMPONENTE PRINCIPAL
const StatusBadge = ({ status }) => {
    // ... (Código do StatusBadge permanece o mesmo)
    let color = 'bg-gray-400';
    if (status === 'Ativo') color = 'bg-green-500';
    if (status === 'Internado') color = 'bg-red-500';
    if (status === 'Monitoramento') color = 'bg-yellow-500';

    return (
        <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium text-white ${color}`}>
            {status}
        </span>
    );
};

// Componente de Ícone com Dropdown
const IconWithDropdown = ({ 
    icon: Icon, 
    title, 
    content, 
    hasAlert = false,
    onClick,
    buttonRef,
    dropdownRef,
    isOpen,
    onMouseEnter,
    onMouseLeave,
    isMobileVersion = false,
    itemKey = null
}) => {
    const [position, setPosition] = useState(null);
    const internalButtonRef = useRef(null);
    const portalMountedRef = useRef(false);

    // Combinar refs: interno e externo
    const combinedButtonRef = (el) => {
        internalButtonRef.current = el;
        if (buttonRef) {
            buttonRef(el);
        }
    };

    // Resetar posição quando fechar
    useEffect(() => {
        if (!isOpen) {
            setPosition(null);
            portalMountedRef.current = false;
        }
    }, [isOpen]);

    // Calcular posição do dropdown quando abrir
    useEffect(() => {
        if (isOpen && internalButtonRef.current) {
            const calculatePosition = () => {
                if (!internalButtonRef.current) return;
                
                const rect = internalButtonRef.current.getBoundingClientRect();
                const isMobile = window.innerWidth < 640; // sm breakpoint
                
                if (isMobile) {
                    // Mobile: dropdown centralizado na tela
                    setPosition({
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        maxWidth: '400px'
                    });
                } else {
                    // Desktop: dropdown posicionado próximo ao ícone
                    // Largura do dropdown: 256px (w-64) no desktop, 320px (w-80) em telas maiores
                    const dropdownWidth = window.innerWidth >= 1024 ? 320 : 256;
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;
                    const spacing = 8; // Espaçamento entre ícone e dropdown
                    
                    // Offset adicional para baixo para alguns itens específicos
                    const itemsWithExtraOffset = ['pressao', 'respiratoria', 'documentos'];
                    const hasExtraOffset = itemsWithExtraOffset.includes(itemKey);
                    const extraOffset = hasExtraOffset ? 10 : 0;
                    
                    // Posicionar o dropdown abaixo do ícone, alinhado à esquerda do ícone
                    let left = rect.left;
                    let top = rect.bottom + spacing + extraOffset;
                    
                    // Verificar se o dropdown cabe na tela
                    // Se não couber pela direita, alinhar à direita do ícone
                    if (left + dropdownWidth > viewportWidth - spacing) {
                        left = rect.right - dropdownWidth;
                    }
                    
                    // Se ainda não couber (ícone muito à direita), posicionar à esquerda do ícone
                    if (left < spacing) {
                        left = rect.left - dropdownWidth - spacing;
                        // Se ainda não couber, centralizar abaixo do ícone
                        if (left < spacing) {
                            const iconCenter = rect.left + (rect.width / 2);
                            left = iconCenter - (dropdownWidth / 2);
                            // Garantir que não saia da tela
                            if (left < spacing) {
                                left = spacing;
                            } else if (left + dropdownWidth > viewportWidth - spacing) {
                                left = viewportWidth - dropdownWidth - spacing;
                            }
                        }
                    }
                    
                    // Se o dropdown sair da tela pela parte inferior, posicionar acima do ícone
                    // Para itens com offset extra, não reposicionar acima (deixar mais para baixo mesmo que saia um pouco)
                    const estimatedHeight = 384; // max-h-96 = 384px
                    if (!hasExtraOffset && top + estimatedHeight > viewportHeight - spacing) {
                        top = rect.top - estimatedHeight - spacing;
                        // Se ainda sair, ajustar para o topo da tela
                        if (top < spacing) {
                            top = spacing;
                        }
                    } else if (hasExtraOffset && top + estimatedHeight > viewportHeight - spacing) {
                        // Para itens com offset extra, apenas garantir que não saia completamente da tela
                        // mas manter o offset adicional
                        const maxTop = viewportHeight - estimatedHeight - spacing;
                        if (top > maxTop) {
                            top = Math.max(spacing, maxTop);
                        }
                    }
                    
                    // Usar getBoundingClientRect() que já retorna coordenadas relativas à viewport
                    // Como estamos usando position: fixed, não precisamos adicionar scrollY
                    setPosition({
                        left: Math.max(spacing, Math.min(left, viewportWidth - dropdownWidth - spacing)),
                        top: Math.max(spacing, top),
                        right: 'auto'
                    });
                    portalMountedRef.current = true;
                }
            };
            
            // Calcular posição usando requestAnimationFrame para garantir que o DOM esteja pronto
            requestAnimationFrame(() => {
                calculatePosition();
            });
            
            // Recalcular ao redimensionar a janela (apenas no desktop)
            if (window.innerWidth >= 640) {
                const handleResize = () => {
                    requestAnimationFrame(() => {
                        calculatePosition();
                    });
                };
                const handleScroll = () => {
                    requestAnimationFrame(() => {
                        calculatePosition();
                    });
                };
                
                window.addEventListener('resize', handleResize);
                window.addEventListener('scroll', handleScroll, true);
                
                return () => {
                    window.removeEventListener('resize', handleResize);
                    window.removeEventListener('scroll', handleScroll, true);
                };
            }
        }
    }, [isOpen, itemKey]);

    const dropdownContent = useMemo(() => {
        if (!isOpen || !position) return null;
        
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
        
        return (
            <>
                {/* Overlay para mobile */}
                {isMobile && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
                        onClick={onMouseLeave}
                    />
                )}
                <div
                    ref={dropdownRef}
                    className={`fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] p-4 overflow-y-auto
                        ${isMobile
                            ? 'w-[90vw] max-w-[400px] max-h-[80vh]' 
                            : 'w-64 sm:w-80 max-h-96'
                        }`}
                    style={{
                        ...(isMobile ? {
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                        } : {
                            left: typeof position.left === 'number' ? `${position.left}px` : position.left,
                            top: typeof position.top === 'number' ? `${position.top}px` : position.top,
                            ...(position.transform && { transform: position.transform }),
                        })
                    }}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={(e) => {
                        // No mobile, prevenir fechamento ao clicar no dropdown
                        if (isMobile) {
                            e.stopPropagation();
                        }
                    }}
                >
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${hasAlert ? 'text-red-600' : 'text-emerald-600'}`} />
                        {title}
                    </h3>
                    <div className="text-sm text-gray-700">
                        {typeof content === 'string' ? <p>{content}</p> : content}
                    </div>
                    {onClick && (
                        <button
                            onClick={() => {
                                onClick();
                            }}
                            className="mt-3 w-full px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                            Ver detalhes →
                        </button>
                    )}
                </div>
            </>
        );
    }, [isOpen, position, title, content, hasAlert, onClick, onMouseEnter, onMouseLeave, Icon, dropdownRef]);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const [shouldRenderPortal, setShouldRenderPortal] = useState(true);
    
    // Verificar se este componente deve renderizar o portal baseado na visibilidade
    useEffect(() => {
        if (!internalButtonRef.current) return;
        
        const checkVisibility = () => {
            const element = internalButtonRef.current;
            if (!element) return;
            
            // Verificar se o elemento está visível
            const rect = element.getBoundingClientRect();
            const style = window.getComputedStyle(element);
            const parent = element.parentElement;
            const parentStyle = parent ? window.getComputedStyle(parent) : null;
            
            const isVisible = 
                rect.width > 0 && 
                rect.height > 0 &&
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                style.opacity !== '0' &&
                (!parentStyle || (parentStyle.display !== 'none' && parentStyle.visibility !== 'hidden'));
            
            setShouldRenderPortal(isVisible);
        };
        
        // Verificar imediatamente e após um pequeno delay para garantir que o CSS foi aplicado
        checkVisibility();
        const timeout = setTimeout(checkVisibility, 50);
        
        // Observar mudanças de tamanho da janela
        window.addEventListener('resize', checkVisibility);
        
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', checkVisibility);
        };
    }, [isOpen, isMobileVersion]);
    
    return (
        <>
            <div 
                className="relative"
                onMouseEnter={!isMobile ? onMouseEnter : undefined}
                onMouseLeave={!isMobile ? onMouseLeave : undefined}
            >
                <button
                    ref={combinedButtonRef}
                    onClick={isMobile ? onMouseEnter : undefined}
                    className={`
                        relative p-2 rounded-lg transition-all duration-200 flex-shrink-0
                        ${hasAlert 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }
                        ${isOpen ? 'bg-gray-100' : ''}
                    `}
                    title={title}
                >
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    {hasAlert && (
                        <>
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white"></span>
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white animate-ping"></span>
                        </>
                    )}
                </button>
            </div>
            {shouldRenderPortal && dropdownContent && typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
        </>
    );
};


const PacienteHeader = ({ pacienteId, onEditPaciente }) => {
    const navigate = useNavigate();
    const { checkPermission } = usePermissions();
    const { data, isLoading, error } = usePacienteProntuario(pacienteId);
    const { status: pagamentoStatus } = usePacientePagamentoStatus(pacienteId);
    const [gerandoPDF, setGerandoPDF] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    
    // Refs para os dropdowns
    const dropdownRefs = useRef({});
    const buttonRefs = useRef({});
    
    // Verificar permissão para acessar planos médicos
    const canAccessPlanos = checkPermission('planos_medicos', 'can_access');
    
    
    const handleMouseEnter = (key) => {
        setOpenDropdown(key);
    };
    
    const handleMouseLeave = () => {
        setOpenDropdown(null);
    };

    if (isLoading) {
        return <LoadingIndicator message="Carregando resumo do paciente..." />;
    }

    if (error || !data || !data.paciente) {
        return <Alert type="error" message={error || "Erro ao carregar informações do paciente."} />;
    }

    const { paciente, proximaMedicao, ultimosSinaisVitais, alertasMedicacao, cuidadorEmPlantao } = data;
    
    // Calcular se há alertas críticos
    const hasCriticalAlerts = alertasMedicacao && alertasMedicacao.length > 0;
    
    // Preparar conteúdo dos dropdowns
    const getIconContent = (key) => {
        switch (key) {
            case 'planos':
                return (
                    <div>
                        <p className="text-lg font-bold text-gray-900">{paciente.total_planos_ativos || 0}</p>
                        <p className="text-gray-600 mt-1">planos médicos ativos</p>
                    </div>
                );
            case 'medicacao':
                if (!proximaMedicao) {
                    return <p className="text-gray-600">Nenhuma medicação agendada</p>;
                }
                return (
                    <div>
                        <p className="font-semibold text-gray-900">{proximaMedicao.nome_medicamento}</p>
                        <p className="text-gray-600 mt-1">
                            {proximaMedicao.dose}{proximaMedicao.unidade_medida}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            Horário: {formatDate(proximaMedicao.hora_agendada, 'time')}
                        </p>
                    </div>
                );
            case 'temperatura':
                const temp = ultimosSinaisVitais?.temperatura;
                return (
                    <div>
                        <p className="text-lg font-bold text-gray-900">{temp || '--'}</p>
                        <p className="text-gray-600 mt-1">°C</p>
                        {ultimosSinaisVitais?.data_registro && (
                            <p className="text-xs text-gray-500 mt-2">
                                {formatDate(ultimosSinaisVitais.data_registro)}
                            </p>
                        )}
                    </div>
                );
            case 'saturacao':
                const sat = ultimosSinaisVitais?.saturacao_o2;
                return (
                    <div>
                        <p className="text-lg font-bold text-gray-900">{sat || '--'}</p>
                        <p className="text-gray-600 mt-1">%</p>
                        {ultimosSinaisVitais?.data_registro && (
                            <p className="text-xs text-gray-500 mt-2">
                                {formatDate(ultimosSinaisVitais.data_registro)}
                            </p>
                        )}
                    </div>
                );
            case 'alertas':
                if (!hasCriticalAlerts) {
                    return <p className="text-gray-600">Nenhum alerta crítico</p>;
                }
                return (
                    <div>
                        <p className="text-lg font-bold text-red-600">{alertasMedicacao.length}</p>
                        <p className="text-gray-600 mt-1">lotes próximos do vencimento</p>
                        <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                            {alertasMedicacao.slice(0, 5).map((alerta, index) => (
                                <div key={index} className="p-2 bg-red-50 rounded border border-red-200">
                                    <p className="text-xs font-semibold text-red-900">{alerta.nome_medicamento}</p>
                                    <p className="text-xs text-red-700">
                                        Lote: {alerta.numero_lote} - Vence: {formatDate(alerta.data_validade)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return <p className="text-gray-600">Informação não disponível</p>;
        }
    };
    
    // Matriz 3x3 de ícones
    const iconMatrix = [
        [
            { key: 'planos', icon: Pill, title: 'Planos Ativos', onClick: canAccessPlanos ? () => navigate(`/pacientes/${pacienteId}/planos`) : null },
            { key: 'medicacao', icon: Calendar, title: 'Próxima Medicação' },
            { key: 'temperatura', icon: Thermometer, title: 'Temperatura' }
        ],
        [
            { key: 'saturacao', icon: Activity, title: 'Saturação O₂' },
            { key: 'alertas', icon: AlertTriangle, title: 'Alertas Críticos', hasAlert: hasCriticalAlerts },
            { key: 'pulso', icon: Heart, title: 'Frequência Cardíaca', content: ultimosSinaisVitais?.freq_cardiaca ? `${ultimosSinaisVitais.freq_cardiaca} bpm` : 'Não registrado' }
        ],
        [
            { key: 'pressao', icon: Stethoscope, title: 'Pressão Arterial', content: ultimosSinaisVitais?.pressao_sistolica ? `${ultimosSinaisVitais.pressao_sistolica}/${ultimosSinaisVitais.pressao_diastolica} mmHg` : 'Não registrado' },
            { key: 'respiratoria', icon: Activity, title: 'Frequência Respiratória', content: ultimosSinaisVitais?.freq_respiratoria ? `${ultimosSinaisVitais.freq_respiratoria} rpm` : 'Não registrado' },
            { key: 'documentos', icon: FileText, title: 'Documentos', content: 'Visualizar documentos do paciente' }
        ]
    ];

    // Função de Download do Prontuário
    const handleDownloadProntuario = async () => {
        setGerandoPDF(true);
        try {
            await gerarProntuarioPDF(pacienteId);
        } catch (err) {
            console.error('Erro ao gerar PDF:', err);
            alert('Erro ao gerar o PDF do prontuário. Por favor, tente novamente.');
        } finally {
            setGerandoPDF(false);
        }
    };
    
    // Função para obter badge de status de pagamento
    const getPagamentoBadge = () => {
        if (!pagamentoStatus) return null;

        const configs = {
            pago: {
                label: 'Pago',
                bgColor: 'bg-green-100',
                textColor: 'text-green-800',
                icon: CheckCircle2,
                borderColor: 'border-green-300'
            },
            atrasado: {
                label: 'Atrasado',
                bgColor: 'bg-red-100',
                textColor: 'text-red-800',
                icon: AlertCircle,
                borderColor: 'border-red-300'
            },
            pendente: {
                label: 'Pendente',
                bgColor: 'bg-yellow-100',
                textColor: 'text-yellow-800',
                icon: Clock,
                borderColor: 'border-yellow-300'
            }
        };

        const config = configs[pagamentoStatus];
        if (!config) return null;

        const Icon = config.icon;

        return (
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-semibold border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
                <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>{config.label}</span>
            </div>
        );
    };

    return (
        <div className="w-full min-w-0">
            {/* Botão de Voltar */}
            <div className="mb-2 sm:mb-3">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium text-sm p-2 rounded-lg hover:bg-emerald-50"
                    title="Voltar"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="hidden sm:inline">Voltar</span>
                </button>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 shadow-lg rounded-lg mb-2 sm:mb-3 sticky top-0 z-10 p-3 sm:p-4 w-full min-w-0 overflow-x-hidden border border-emerald-200">
                {/* Linha Principal: Info do Paciente e Botões */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 border-b border-emerald-200 pb-3 w-full min-w-0">
                
                {/* Informações do Paciente */}
                <div className={`flex items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden ${cuidadorEmPlantao ? 'sm:items-start' : 'sm:items-center'}`}>
                    {/* Container de Fotos: Paciente e Cuidador */}
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                        {/* Foto do Paciente */}
                        <img 
                            className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover border-2 sm:border-4 border-white shadow-md"
                            src={getPacienteFotoUrl(paciente.fotoURL)} 
                            alt={`Foto de ${paciente.nome_completo}`}
                            onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = FALLBACK_AVATAR; 
                            }}
                        />
                        
                        {/* Setas de Troca */}
                        {cuidadorEmPlantao && (
                            <div className="flex flex-col items-center gap-0.5">
                                <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                                <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                            </div>
                        )}
                        
                        {/* Foto do Cuidador de Plantão */}
                        {cuidadorEmPlantao && (
                            <img 
                                className="h-10 w-10 sm:h-14 sm:w-14 rounded-full object-cover border-2 sm:border-3 border-emerald-300 shadow-md"
                                src={getUsuarioFotoUrl(cuidadorEmPlantao.fotoURL)} 
                                alt={`Foto de ${cuidadorEmPlantao.nome_completo}`}
                                title={`Cuidador de plantão: ${cuidadorEmPlantao.nome_completo}`}
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = FALLBACK_AVATAR; 
                                }}
                            />
                        )}
                    </div>

                    {/* Dados Básicos */}
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 flex-wrap">
                            <h1 className="text-lg sm:text-2xl font-extrabold text-gray-900 flex items-center gap-2 flex-wrap">
                                <span className="truncate">{paciente.nome_completo}</span>
                                <StatusBadge status={paciente.status} />
                                {getPagamentoBadge()}
                            </h1>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-700 mt-1">
                            Nascimento: {paciente.data_nascimento_formatada} ({paciente.idade} anos)
                        </p>
                        <p className="text-xs sm:text-sm font-semibold text-emerald-700 truncate mt-1">
                            {paciente.condicao_medica || 'Condição não registrada'}
                        </p>
                        {cuidadorEmPlantao && (
                            <p className="text-xs sm:text-sm text-emerald-600 mt-12">
                                Cuidador de plantão: <span className="font-semibold">{cuidadorEmPlantao.nome_completo}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Desktop: Matriz 3x3 de Ícones e Botões de Ação */}
                <div className="hidden sm:flex items-center gap-3 sm:gap-4 flex-shrink-0">
                    {/* Matriz 3x3 de Ícones */}
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 ml-6 sm:ml-8 md:ml-12">
                        {iconMatrix.map((row, rowIndex) => (
                            <React.Fragment key={rowIndex}>
                                {row.map((item) => {
                                    const content = item.content 
                                        ? (typeof item.content === 'string' ? <p className="text-gray-600">{item.content}</p> : item.content)
                                        : getIconContent(item.key);
                                    return (
                                        <IconWithDropdown
                                            key={item.key}
                                            icon={item.icon}
                                            title={item.title}
                                            content={content}
                                            hasAlert={item.hasAlert}
                                            onClick={item.onClick}
                                            buttonRef={(el) => (buttonRefs.current[item.key] = el)}
                                            dropdownRef={(el) => (dropdownRefs.current[item.key] = el)}
                                            isOpen={openDropdown === item.key}
                                            onMouseEnter={() => handleMouseEnter(item.key)}
                                            onMouseLeave={handleMouseLeave}
                                            isMobileVersion={false}
                                            itemKey={item.key}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                    
                    {/* Botões de Ação */}
                    <div className="flex flex-col items-center gap-2 flex-shrink-0 ml-2 border-l border-emerald-200 pl-3">
                        {/* Botão Editar */}
                        <button
                            onClick={() => (onEditPaciente ? onEditPaciente(paciente) : alert('Editar Paciente...'))}
                            className="p-2 sm:p-2.5 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                            title="Editar Paciente"
                        >
                            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        
                        {/* Botão Download */}
                        <button
                            onClick={handleDownloadProntuario}
                            className="p-2 sm:p-2.5 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={gerandoPDF ? 'Gerando PDF...' : 'Baixar Prontuário (PDF)'}
                            disabled={gerandoPDF}
                        >
                            <CloudDownload className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>

                {/* Mobile: Botões de Ação no Topo */}
                <div className="flex sm:hidden items-center justify-end gap-2 flex-shrink-0">
                    {/* Botão Editar */}
                    <button
                        onClick={() => (onEditPaciente ? onEditPaciente(paciente) : alert('Editar Paciente...'))}
                        className="p-2 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                        title="Editar Paciente"
                    >
                        <Pencil className="w-5 h-5" />
                    </button>
                    
                    {/* Botão Download */}
                    <button
                        onClick={handleDownloadProntuario}
                        className="p-2 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={gerandoPDF ? 'Gerando PDF...' : 'Baixar Prontuário (PDF)'}
                        disabled={gerandoPDF}
                    >
                        <CloudDownload className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Mobile: Matriz de Ícones em Linha na Parte Inferior */}
            <div className="sm:hidden border-t border-emerald-200 pt-3">
                <div className="flex flex-wrap justify-center gap-2">
                    {iconMatrix.flat().map((item) => {
                        const content = item.content 
                            ? (typeof item.content === 'string' ? <p className="text-gray-600">{item.content}</p> : item.content)
                            : getIconContent(item.key);
                        return (
                            <IconWithDropdown
                                key={item.key}
                                icon={item.icon}
                                title={item.title}
                                content={content}
                                hasAlert={item.hasAlert}
                                onClick={item.onClick}
                                buttonRef={(el) => (buttonRefs.current[item.key] = el)}
                                dropdownRef={(el) => (dropdownRefs.current[item.key] = el)}
                                isOpen={openDropdown === item.key}
                                onMouseEnter={() => handleMouseEnter(item.key)}
                                onMouseLeave={handleMouseLeave}
                                isMobileVersion={true}
                                itemKey={item.key}
                            />
                        );
                    })}
                </div>
            </div>
            </div>
        </div>
    );
};

export default PacienteHeader;